import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BiCheckCircle, BiLoader, BiArrowBack } from 'react-icons/bi';
import { authService } from '../services/authService';
import Alert from './Alert';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as yup from 'yup';
import { buildAuthUrl } from '../utils/url';

interface EmailVerificationProps {
  email: string;
  redirectUrl?: string | null;
  onBack: () => void;
  onResend: () => void;
}

const verificationSchema = yup.object({
  confirmationCode: yup
    .string()
    .required('Confirmation code is required')
    .matches(/^\d{6}$/, 'Confirmation code must be 6 digits'),
});

interface VerificationFormData {
  confirmationCode: string;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({ email, redirectUrl, onBack }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | 'info'>('info');

  const initialValues: VerificationFormData = {
    confirmationCode: '',
  };

  const handleSubmit = async (values: VerificationFormData, { setSubmitting }: any) => {
    setMessage('');
    setMessageType('info');
    setIsLoading(true);

    try {
      const response = await authService.verifyEmail(email, values.confirmationCode);

      if (response.success) {
        const loginUrl = buildAuthUrl('/auth/login', redirectUrl);
        setMessage('Email verified successfully! Redirecting to login...');
        setMessageType('success');
        setTimeout(() => navigate(loginUrl), 2000);
      } else {
        setMessage(response.message || 'Failed to verify email. Please try again.');
        setMessageType('error');
      }
    } catch (error: any) {
      setMessage(error.message || 'Failed to verify email. Please try again.');
      setMessageType('error');
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8 space-y-6">
        <div className="text-center">
          <div className="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900 rounded-full flex items-center justify-center mb-4">
            <BiCheckCircle className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Verify Your Email</h2>
          <p className="text-slate-500 dark:text-slate-400 mt-2">
            We've sent a verification code to <strong>{email}</strong>
          </p>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            Please check your email and enter the 6-digit code below.
          </p>
        </div>

        {message && <Alert type={messageType} message={message} onClose={() => setMessage('')} />}

        <Formik
          initialValues={initialValues}
          validationSchema={verificationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <Field
                  name="confirmationCode"
                  type="text"
                  placeholder="Enter 6-digit code"
                  maxLength="6"
                  className={`w-full px-4 py-3 text-center text-2xl font-mono border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-slate-100 dark:bg-slate-700 text-slate-900 dark:text-white ${errors.confirmationCode && touched.confirmationCode ? 'border-red-500 focus:ring-red-500' : 'border-slate-200 dark:border-slate-600'
                    }`}
                />
                <ErrorMessage name="confirmationCode" component="p" className="text-red-500 text-sm mt-1 text-center" />
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting || isLoading}
                  className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  {isSubmitting || isLoading ? (
                    <>
                      <BiLoader className="animate-spin mr-2" />
                      Verifying...
                    </>
                  ) : (
                    'Verify Email'
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>

        <div className="text-center space-y-3">
          <div className="text-xs text-slate-400 dark:text-slate-500">
            <p>Didn't receive the code?</p>
            <p>Check your spam folder or try registering again.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification; 